import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ChefCard from "@/components/ChefCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import Seo from "@/components/Seo";

const CITIES = ["All", "Mumbai", "Bengaluru", "Indore", "Chennai", "Goa", "Dubai"];
const CUISINES = ["All", "Modern Indian", "Coastal", "South Indian", "Hyderabadi", "Italian", "Japanese", "Chettinad", "North Indian"];
const ROSTER = ["All", "indian", "international"];

export default function Chefs() {
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("All");
  const [cuisine, setCuisine] = useState("All");
  const [salaryType, setSalaryType] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (city !== "All") params.city = city;
      if (cuisine !== "All") params.cuisine = cuisine;
      if (salaryType !== "All") params.salary_type = salaryType;
      const r = await api.get("/chefs", { params });
      setChefs(r.data.chefs);
      setLoading(false);
    })();
  }, [city, cuisine, salaryType]);

  const filtered = useMemo(() => {
    if (!q) return chefs;
    return chefs.filter(c => (c.name + " " + c.cuisines.join(" ") + " " + c.signature_dishes.join(" ")).toLowerCase().includes(q.toLowerCase()));
  }, [chefs, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <Seo
        title="Private Chefs in Mumbai, Bengaluru & Indore from ₹75,000/mo | Chef Hire"
        description="Browse our curated roster of background-verified private chefs — from ₹75,000/month. Healthy cooking, curated household meals and innovative food for HNI homes in Mumbai, Bengaluru & Indore."
        path="/chefs"
        keywords="private chef Mumbai, chef hire Mumbai, home chef Bengaluru, private chef Indore, hire a cook India"
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="small-caps text-neutral-500 mb-3">The Roster</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none">Private chefs.</h1>
          <p className="text-neutral-400 mt-4 max-w-xl">Full-time postings for HNI homes — from ₹75,000/month. Shortlisted, vetted, ready.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="chip text-[#F5B041] border-[#F5B041]/40">From ₹75,000/mo</span>
            <span className="chip">Healthy cooking</span>
            <span className="chip">Curated meals</span>
            <span className="chip">Innovative food</span>
          </div>
        </div>
        <div className="text-right">
          <div className="small-caps text-[10px] text-neutral-500">Available now</div>
          <div className="font-serif-menu text-2xl">{loading ? "—" : `${chefs.length} chefs`}</div>
        </div>
      </div>

      <div className="dotted-sep my-10" />

      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-4 top-3.5 text-neutral-500" />
          <Input
            data-testid="chef-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, cuisine, dish…"
            className="pl-10 h-11 bg-transparent border-white/10 rounded-none focus-visible:border-[#C04000] focus-visible:ring-0"
          />
        </div>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger data-testid="city-filter" className="h-11 rounded-none bg-transparent border-white/10">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[#141414] border-white/10">
            {CITIES.map((c) => <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={salaryType} onValueChange={setSalaryType}>
          <SelectTrigger data-testid="roster-filter" className="h-11 rounded-none bg-transparent border-white/10">
            <SelectValue placeholder="Roster" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[#141414] border-white/10">
            {ROSTER.map((c) => <SelectItem key={c} value={c} className="rounded-none capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4">
        <Select value={cuisine} onValueChange={setCuisine}>
          <SelectTrigger data-testid="cuisine-filter" className="h-11 rounded-none bg-transparent border-white/10 md:w-64">
            <SelectValue placeholder="Cuisine" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-[#141414] border-white/10">
            {CUISINES.map((c) => <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 stagger-in" data-testid="chefs-grid">
        {loading ? Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="card-lux">
            <Skeleton className="aspect-[4/5] w-full rounded-none bg-neutral-900" />
            <div className="p-5"><Skeleton className="h-6 w-1/2 bg-neutral-800" /></div>
          </div>
        )) : filtered.map((c) => <ChefCard key={c.id} chef={c} />)}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="text-center text-neutral-500 py-24">
          <div className="font-serif-menu text-2xl">No chefs match.</div>
          <p className="mt-2 text-sm">Try a different city or cuisine — or write to us at hello@chefhire.in</p>
        </div>
      )}
    </div>
  );
}
